import { Injectable, inject } from '@angular/core';
import { NativeService } from '@shared/services/native.service';
import { LinkLogin, LoginProviders, User } from '@shared/models/user.model';
import { FacebookService } from '@shared/services/facebook.service';
import { UserService } from '@shared/services/user.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { first } from 'rxjs/operators';
import { AvatarService } from '@shared/components/avatar/avatar.service';
import { environment } from '@environments/environment';
import { LoggerService } from '@shared/services/logger.service';
import { lastValueFrom, Subject } from 'rxjs';
import { AppleService } from '@shared/services/apple.service';
import { GooglePlayGamesService } from '@shared/services/googlePlayGames.service';

interface LinkLoginParams {
  providerKey: string;
  accessToken?: string;
}

interface FacebookLinkLoginParams extends LinkLoginParams {
  providerKey: string;
  accessToken?: string;
  autheticationToken?: string;
}

interface AppleLinkLoginParams extends LinkLoginParams {
  email?: string;
  userName?: string;
}

@Injectable({
  providedIn: 'root',
})
export class LinkLoginService {
  private http = inject(HttpClient);
  logger = inject(LoggerService);
  private nativeService = inject(NativeService);
  private facebookService = inject(FacebookService);
  private appleService = inject(AppleService);
  private googlePlayGamesService = inject(GooglePlayGamesService);
  private userService = inject(UserService);
  private avatarService = inject(AvatarService);

  private apiLinkLoginUrl = `${environment.apiUrl}/api/linklogin`;

  private linkLoginErrorSource = new Subject<LinkLogin>();
  linkLoginError$ = this.linkLoginErrorSource.asObservable();

  async link(loginProvider: LoginProviders) {
    if (loginProvider === 'Facebook') {
      await this.linkWithFacebook();
    } else if (loginProvider === 'AppleId') {
      await this.linkWithApple();
    } else if (loginProvider === 'GooglePlay') {
      await this.linkWithGooglePlay();
    }
  }

  private async linkWithFacebook() {
    const authStatus = await this.facebookService.fetchLoginStatus();
    await this.linkWith('Facebook', {
      providerKey: authStatus.userID,
      accessToken: authStatus.accessToken,
      authenticationToken: authStatus.authenticationToken,
    } as FacebookLinkLoginParams);
  }

  private async linkWithApple() {
    const authStatus = await this.appleService.doLogin();
    await this.linkWith('AppleId', {
      providerKey: authStatus.userIdentifier,
      email: authStatus.email,
      userName: authStatus.userName,
      accessToken: authStatus.identityToken,
    } as AppleLinkLoginParams);
  }

  private async linkWithGooglePlay() {
    const player = await this.googlePlayGamesService.fetchPlayer();
    await this.linkWith('GooglePlay', {
      providerKey: player.playerId,
      displayName: player.displayName,
      iconImageUri: player.iconImageUri,
      hiResImageUri: player.hiResImageUri,
    } as AppleLinkLoginParams);
  }

  private async linkWith(loginProvider: LoginProviders, linkLoginParams: LinkLoginParams) {
    try {
      await lastValueFrom(this.http.post(`${this.apiLinkLoginUrl}/${loginProvider}`, linkLoginParams));
    } catch (e) {
      this.onLinkError(loginProvider, e);
      return;
    }

    this.loginLinked(loginProvider);
  }

  private onLinkError(loginProvider: LoginProviders, e) {
    this.logger.info(`Error linking the account: ${JSON.stringify(e)}`);
    if (e instanceof HttpErrorResponse) {
      this.linkLoginErrorSource.next({
        loginProvider: loginProvider,
        errorCode: e.status,
      });
    }
  }

  private loginLinked(loginProvider: LoginProviders) {
    this.userService.currentUser$.pipe(first()).subscribe({
      next: (user: User) => {
        switch (loginProvider) {
          case 'Facebook':
            user.hasFacebook = true;
            break;
          case 'AppleId':
            user.hasAppleId = true;
            break;
          case 'GooglePlay':
            user.hasGooglePlay = true;
            break;
        }
        this.avatarService.notifyAvatar(user.id);
      },
      error: (error) => this.logger.error(error),
    });
  }
}
